export default function AboutSection() {
  const milestones = [
    {
      icon: "ri-building-line",
      year: "2013",
      text: "Built and operated a title company from the ground up — experiencing firsthand how deals are actually won.",
    },
    {
      icon: "ri-phone-line",
      year: "Year 1–3",
      text: "Built the entire market through cold calling — earning every relationship, one conversation at a time.",
    },
    {
      icon: "ri-cpu-line",
      year: "Embraced Technology",
      text: "Adopted every available technology to gain an edge — but outbound remained manual, inconsistent, and impossible to scale.",
    },
    {
      icon: "ri-robot-line",
      year: "Today",
      text: "Built Imperium to give title companies the advantage that used to take years to build — now delivered instantly, consistently, and at scale.",
    },
  ];

  return (
    <section id="about" className="bg-white py-14 md:py-24 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left: Text */}
          <div>
            <div className="inline-flex items-center gap-2 bg-alt-bg border border-gray-200 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-team-line text-navy text-xs" />
              <span className="text-xs font-semibold text-navy">Built for Title, by Title</span>
            </div>

            <h2
              className="text-navy font-semibold mb-5"
              style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: "1.2" }}
            >
              Built by Title Operators. Proven in the Real World.
            </h2>
            <p className="text-[#4a5568] text-lg mb-6" style={{ lineHeight: "1.65" }}>
              Imperium wasn&apos;t built by software engineers guessing how the title industry works.
              It was built by someone who spent over 13 years in it — making the calls, building
              relationships, and scaling a title company market by market.
            </p>
            <p className="text-[#4a5568] text-base mb-6 md:mb-10" style={{ lineHeight: "1.65" }}>
              Every part of Imperium exists because it solves a real problem — one that had to be
              solved manually, one call at a time.
            </p>

            {/* James's quote */}
            <blockquote className="bg-alt-bg rounded-2xl p-6 border-l-4 border-primary mb-4 md:mb-8">
              <p className="text-navy text-base italic leading-relaxed mb-4" style={{ lineHeight: "1.7" }}>
                &ldquo;I built my entire market one cold call at a time. I know how hard it is to
                stay top of mind. Imperium does in minutes what used to take weeks.&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-navy/10 flex-shrink-0">
                  <img
                    src="https://storage.readdy-site.link/project_files/a5e9c8d2-9c4d-45a2-a3e3-a7805868b9ce/b5e8d2e5-3773-4316-9331-289c9ccf9788_MDP_5507_EDIT2.png?v=8ce0c3518112be2bf32e637bb0d8021a"
                    alt="James P. Schlimmer"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="text-navy font-semibold text-sm">James P. Schlimmer</p>
                  <p className="text-[#4a5568] text-xs">Founder, Imperium · 13+ Year Title Company Owner &amp; Operator</p>
                </div>
              </div>
            </blockquote>
          </div>

          {/* Right: Timeline */}
          <div>
            <h3 className="text-navy font-semibold text-base mb-5 md:mb-8">The Journey Behind Imperium</h3>
            <div className="relative pl-6">
              {/* Vertical line */}
              <div className="absolute left-2.5 top-0 bottom-0 w-px bg-gray-200" />

              <div className="flex flex-col gap-8">
                {milestones.map((milestone, index) => (
                  <div key={index} className="relative flex items-start gap-5">
                    {/* Dot on timeline */}
                    <div className="absolute -left-6 top-0 w-5 h-5 flex items-center justify-center bg-primary rounded-full border-2 border-white z-10 flex-shrink-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-white" />
                    </div>

                    <div className="bg-alt-bg rounded-xl p-5 border border-gray-100 w-full">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-gray-100 flex-shrink-0">
                          <i className={`${milestone.icon} text-primary text-sm`} />
                        </div>
                        <div>
                          <span className="text-primary text-xs font-bold uppercase tracking-wide">{milestone.year}</span>
                          <p className="text-navy text-sm leading-relaxed mt-1">{milestone.text}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Why Imperium is different label */}
            <p className="text-navy text-xs font-bold uppercase tracking-wider mt-8 md:mt-10 mb-4">Why Imperium is different:</p>

            {/* Credential badges */}
            <div className="flex flex-wrap gap-3">
              {[
                { icon: "ri-building-line", text: "13+ Years in Title" },
                { icon: "ri-map-line", text: "Statewide Operations" },
                { icon: "ri-phone-line", text: "Built Market via Cold Calling" },
                { icon: "ri-shield-check-line", text: "SOC 2 Type II Platform" },
              ].map((badge) => (
                <div
                  key={badge.text}
                  className="flex items-center gap-2 bg-alt-bg border border-gray-200 rounded-full px-3.5 py-1.5"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${badge.icon} text-navy text-xs`} />
                  </span>
                  <span className="text-navy text-xs font-medium">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom closing line */}
        <div className="mt-8 md:mt-14 text-center">
          <p className="text-[#4a5568] text-sm font-medium italic">
            This isn&apos;t theory — this is the system built from real experience to win more closings.
          </p>
        </div>
      </div>
    </section>
  );
}
